import { useParams, useSearchParams } from 'react-router'

import { SkeletonRows } from '@/components/ui/skeleton'
import { ClaimStatus, useClaim } from '../../../../models/claim'
import { useFeatureFlag } from '../../../../models/flag'
import { useCurrentUser } from '../../../../models/user'
import { ClaimForm } from './ClaimForm'
import { ClaimLifecycle } from './ClaimLifecycle'
import { ClaimReviewForm } from './ClaimReviewForm'

export function EditClaim() {
  const { claimId } = useParams<{ claimId: string }>()
  // CAI staff editing a customer's claim pass ?org_id= so the request is org-scoped.
  // ?view=1 forces the read-only summary (the table's "View" link).
  const [searchParams] = useSearchParams()
  const orgId = searchParams.get('org_id') ?? undefined
  const forceView = searchParams.get('view') === '1'

  const { data: claim, isLoading } = useClaim(claimId, orgId)
  const lifecycleEnabled = useFeatureFlag('ENABLE_CLAIM_LIFECYCLE')
  const { user, isLoading: userLoading } = useCurrentUser({ enabled: true })

  if (isLoading || userLoading) return <SkeletonRows className="mt-10" rows={6} />
  if (!claim) return <div className="mt-10 text-center">Claim not found</div>

  // Drafts are editable by anyone; CAI admins can edit any claim regardless of
  // status. Everyone else gets the read-only summary once submitted.
  const canEdit = claim.status === ClaimStatus.DRAFT || (user?.isCaiAdmin ?? false)

  if (forceView || !canEdit) {
    if (!lifecycleEnabled)
      return <ClaimReviewForm claimId={claim.id} orgId={orgId} onBack={() => undefined} />
    return (
      <div className="mx-auto flex w-full max-w-5xl flex-col lg:flex-row lg:items-start lg:gap-8">
        <aside className="px-4 pt-6 lg:w-80 lg:shrink-0 lg:px-0 lg:py-6">
          <div className="mb-1 font-mono text-xs uppercase">Claim {claim.idShort}</div>
          <h1 className="mb-6 text-xl font-bold tracking-[-0.01em]">Where your claim stands</h1>
          <ClaimLifecycle claim={claim} />
        </aside>
        <div className="min-w-0 flex-1">
          <ClaimReviewForm
            claimId={claim.id}
            orgId={orgId}
            onBack={() => undefined}
            showHeader={claim.status === ClaimStatus.DRAFT}
          />
        </div>
      </div>
    )
  }
  return <ClaimForm claim={claim} orgId={orgId} />
}
